/**
 * The variant matrix, pure: cartesian generation, edit-preserving merge, and
 * derived stock state. No DB, no clock — everything here is unit-testable and
 * shared verbatim by the form (client-side generation) and the domain
 * (server-side validation), so the two can't disagree about what a
 * combination is.
 *
 * @see docs/spec/47-store-product-variants.md R2–R4
 */

/** One axis of the matrix: a name and its ordered values. */
export interface VariantAttribute {
  name: string;
  values: string[];
}

/**
 * One combination, as the form and the wire carry it.
 *
 * No quantity field: stock on hand is **derived** from the bundles that
 * contain the variant (`variantQtyFromBundles`), never typed here.
 */
export interface VariantDraft {
  /** One value per axis, keyed by axis name. The row's identity. */
  attributeValues: Record<string, string>;
  sku: string;
  barcode: string;
  /** Stringly on the form (free text inputs); parsed at the wire boundary. */
  weightGrams: string;
}

/**
 * Hard cap on generated rows. 4 axes × ~6 values ≈ real catalogues; 100 rows
 * is far above any legitimate garment matrix and stops a typo ("one value per
 * keystroke") from generating thousands of rows into form state.
 */
export const MAX_VARIANTS = 100;

/** Axis cap — mirrors the contract. */
export const MAX_ATTRIBUTES = 4;

/**
 * The stable identity of a combination. Key order must not matter — the same
 * axes reordered are the same variant — so keys are sorted before joining.
 * Values are matched case-sensitively: "m" and "M" are different values by the
 * operator's own declaration in the builder (which dedupes case-insensitively,
 * so the pair can't normally coexist).
 */
export function variantKey(attributeValues: Record<string, string>): string {
  return Object.keys(attributeValues)
    .sort()
    .map((k) => `${k}=${attributeValues[k]}`)
    .join(" | ");
}

/**
 * Default SKU for a combination: `<base>-<VALUE>-<VALUE>` with each value
 * upper-cased and stripped to alphanumerics, truncated to 8 chars per segment
 * ("Black" → BLACK, "Extra Large" → EXTRALAR). The mockup's `SKU-<value>`
 * pattern, generalised to multiple axes. Purely a default — every SKU is
 * operator-editable afterwards.
 */
export function defaultVariantSku(base: string, attributeValues: Record<string, string>): string {
  const segments = Object.keys(attributeValues)
    .sort()
    .map((k) =>
      (attributeValues[k] ?? "")
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "")
        .slice(0, 8),
    )
    .filter((s) => s.length > 0);
  return [base || "SKU", ...segments].join("-").slice(0, 64);
}

/**
 * Every combination of the axes' values, in axis-then-value order, as fresh
 * drafts with defaulted SKUs and blank barcode/weight/qty.
 *
 * Axes with no name or no values contribute nothing (a half-filled builder row
 * must not zero the whole matrix — skipping is the honest reading of "not
 * finished yet"). Returns `[]` when no complete axis exists.
 */
export function generateVariants(
  attributes: readonly VariantAttribute[],
  baseSku: string,
): VariantDraft[] {
  const axes = attributes
    .map((a) => ({ name: a.name.trim(), values: a.values.filter((v) => v.trim().length > 0) }))
    .filter((a) => a.name.length > 0 && a.values.length > 0);
  if (axes.length === 0) return [];

  let combos: Record<string, string>[] = [{}];
  for (const axis of axes) {
    combos = combos.flatMap((c) => axis.values.map((v) => ({ ...c, [axis.name]: v })));
    if (combos.length > MAX_VARIANTS) break; // caller shows the cap message
  }

  return combos.slice(0, MAX_VARIANTS).map((attributeValues) => ({
    attributeValues,
    sku: defaultVariantSku(baseSku, attributeValues),
    barcode: "",
    weightGrams: "",
  }));
}

export interface MergeResult {
  variants: VariantDraft[];
  added: number;
  removed: number;
  kept: number;
  /** Combinations skipped because the operator had deleted their rows. */
  leftOut: number;
}

/**
 * Regeneration that respects the operator's work, deletions included.
 *
 * For every combination that still exists, the *existing* row (with its
 * edited SKU/barcode/weight/qty) wins; combinations no longer produced are
 * dropped. A generated combination with no existing row is added only when
 * BOTH deletion signals clear it; otherwise it counts as `leftOut` so the
 * toast can say so:
 *
 * 1. **`deletedKeys`** — the combinations the operator deleted this session
 *    (`variantKey`-ed), passed by the form. Authoritative while present: a
 *    tombstoned combination never regenerates. The caller prunes a tombstone
 *    when any of its values leaves the axis chips — which is what makes the
 *    restore path work: remove and re-add the value's chip, and its
 *    combinations (tombstones gone) regenerate.
 * 2. **The row-derived rule** — with no tombstone, a combination is re-added
 *    only when it involves a *new value*, one no existing row uses. This is
 *    what preserves deletions across a save/reload, where session tombstones
 *    are gone. It cannot protect a single-axis matrix (deleting value M's
 *    only row makes M indistinguishable from a value just added) — that gap
 *    is exactly why signal 1 exists.
 *
 * Documented consequence: after a reload, regenerating a *single-axis* matrix
 * re-adds combinations that were deleted in an earlier session. The chips —
 * not the rows — are the durable source of truth for which values exist; a
 * value you don't stock at all should lose its chip.
 */
export function mergeVariants(
  existing: readonly VariantDraft[],
  generated: readonly VariantDraft[],
  deletedKeys?: ReadonlySet<string>,
): MergeResult {
  const byKey = new Map(existing.map((v) => [variantKey(v.attributeValues), v]));

  // Which values does the current row set actually use, per axis?
  const valuesInUse = new Map<string, Set<string>>();
  for (const v of existing) {
    for (const [axis, value] of Object.entries(v.attributeValues)) {
      const set = valuesInUse.get(axis) ?? new Set<string>();
      set.add(value);
      valuesInUse.set(axis, set);
    }
  }

  const involvesNewValue = (attributeValues: Record<string, string>): boolean =>
    Object.entries(attributeValues).some(
      ([axis, value]) => !(valuesInUse.get(axis)?.has(value) ?? false),
    );

  let added = 0;
  let kept = 0;
  let leftOut = 0;
  const variants: VariantDraft[] = [];
  for (const g of generated) {
    const key = variantKey(g.attributeValues);
    const match = byKey.get(key);
    if (match) {
      kept += 1;
      variants.push(match);
      continue;
    }
    const tombstoned = deletedKeys?.has(key) ?? false;
    if (!tombstoned && (existing.length === 0 || involvesNewValue(g.attributeValues))) {
      added += 1;
      variants.push(g);
      continue;
    }
    leftOut += 1;
  }
  return { variants, added, kept, removed: existing.length - kept, leftOut };
}

// ── derived stock state ───────────────────────────────────────────

export type VariantStockState = "untracked" | "in-stock" | "low-stock" | "out-of-stock";

/**
 * Below this, a tracked variant reads "Low stock". A fixed floor rather than a
 * per-variant setting: the pill is a glance-level warning, not a reorder
 * policy — that lives with the inventory module when variants join the ledger.
 */
export const LOW_STOCK_THRESHOLD = 10;

/**
 * Availability, derived — never stored. The same discipline as
 * `listingStockState`: quantity is the fact, the label is a reading of it.
 * `undefined`/blank = untracked, which renders nothing.
 */
export function variantStockState(qtyOnHand: number | undefined): VariantStockState {
  if (qtyOnHand === undefined || Number.isNaN(qtyOnHand)) return "untracked";
  if (qtyOnHand <= 0) return "out-of-stock";
  if (qtyOnHand < LOW_STOCK_THRESHOLD) return "low-stock";
  return "in-stock";
}

/** The two fields of a pricing entry this derivation reads. Structural, so the
 *  domain doesn't depend on the form's or the wire's entry type. */
export interface BundleStockSource {
  /** How many of this bundle the seller holds. Absent = untracked. */
  packsOnHand?: number | undefined;
  /** Per-variant breakdown of ONE bundle. Absent on a size-composed bundle. */
  variantComposition?:
    | readonly { attributeValues: Record<string, string>; qty: number }[]
    | undefined;
}

/**
 * Pieces of each variant on hand, derived from the bundles that contain it —
 * `Σ over entries of (pieces of this variant in one bundle × bundles held)`.
 * Keyed by `variantKey`.
 *
 * **An absent key means untracked, and that is load-bearing** (spec 47 R5:
 * absent ≠ 0). Returning 0 for "no bundle mentions this variant" would paint
 * every variant of an unpriced product as Out of stock. So an entry
 * contributes only when it has BOTH a `variantComposition` and a
 * `packsOnHand`; a variant no contributing entry mentions stays absent, while
 * one held at `packsOnHand: 0` correctly resolves to 0.
 *
 * Size-composed bundles (a `sizeRatio` and nothing finer) contribute nothing:
 * their pieces are attributable to a size but not to a colour, and `colorSpec`
 * carries no per-colour quantity. Splitting them would invent data.
 *
 * This models stock as whatever is committed to bundles — loose pieces are
 * invisible, and pieces backing two entries count twice. That is the
 * bundle-first catalogue's own definition of "how many do I have", not an
 * approximation of a ledger (ADR-0061).
 */
export function variantQtyFromBundles(
  entries: readonly BundleStockSource[],
): Map<string, number> {
  const byKey = new Map<string, number>();
  for (const entry of entries) {
    if (entry.packsOnHand === undefined || !entry.variantComposition) continue;
    for (const line of entry.variantComposition) {
      const key = variantKey(line.attributeValues);
      byKey.set(key, (byKey.get(key) ?? 0) + line.qty * entry.packsOnHand);
    }
  }
  return byKey;
}
